import React, { useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import AccordionBtn, { AccordionProps } from 'components/commercetools-ui/atoms/accordion';
import CloseIcon from 'components/icons/close';
import useClassNames from 'helpers/hooks/useClassNames';
import { useFormat } from 'helpers/hooks/useFormat';
import { Discount } from 'types/entity/cart';

export interface Props {
  className?: string;
  discounts: Discount[];
  onApplyDiscountCode?: (code: string) => Promise<void>;
  onRemoveDiscountCode?: (discount: Discount) => Promise<void>;
}

const DiscountForm: React.FC<Props> = ({ className, discounts, onApplyDiscountCode, onRemoveDiscountCode }) => {
  const { formatMessage: formatCartMessage } = useFormat({ name: 'cart' });

  const [code, setCode] = useState('');
  const [codeIsInvalid, setCodeIsInvalid] = useState(false);
  const [erroMessage, setErrorMessage] = useState('');

  const [processing, setProcessing] = useState(false);

  const inputClassName = useClassNames([
    'h-40 w-full border  rounded-[5px] px-10 py-10 text-[14px] placeholder:text-secondary-black disabled:bg-neutral-300 focus:outline-none pl-32',
    codeIsInvalid ? 'border-accent-red text-accent-red focus:border-accent-red' : 'border-gray-400',
  ]);

  const discountsContainerClassName = useClassNames([
    'mt-8 flex flex-wrap justify-items-start gap-12',
    discounts?.length === 0 ? 'pt-0' : 'pt-4',
  ]);

  const containerClassName = useClassNames(['text-16 border-t border-neutral-400 text-14', className]);

  const onApplyDiscount = () => {
    if (processing || !code) return;

    setProcessing(true);

    onApplyDiscountCode?.(code)
      .then(() => setCode(''))
      .catch((err: Error) => {
        if (err.message.includes('MaxApplicationReached')) {
          setErrorMessage(
            formatCartMessage({
              id: 'voucher.max.usage.singular',
              defaultMessage:
                'This discount code can no longer be redeemed as the maximum application has been reached. {codes}',
              values: { codes: '' },
            }),
          );
        } else {
          setErrorMessage(formatCartMessage({ id: 'codeNotValid', defaultMessage: 'The discount code is not valid' }));
        }

        setCodeIsInvalid(true);
      })
      .finally(() => {
        setProcessing(false);
      });
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCode(e.target.value);
    setCodeIsInvalid(false);
  };

  const handleRemove = (discount: Discount) => {
    onRemoveDiscountCode?.(discount);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onApplyDiscount();
  };

  const resetCode = () => {
    setCode('');
    setCodeIsInvalid(false);
  };

  return (
    <div className={containerClassName}>
      <form onSubmit={handleSubmit}>
        <div className="relative">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="14"
            height="16"
            viewBox="0 0 14 16"
            fill="none"
            className="absolute left-12 top-1/2 -translate-y-1/2 text-black"
          >
            <path
              d="M0 2.5V7.17187C0 7.70312 0.209375 8.2125 0.584375 8.5875L6.08438 14.0875C6.86563 14.8687 8.13125 14.8687 8.9125 14.0875L13.0844 9.91562C13.8656 9.13437 13.8656 7.86875 13.0844 7.0875L7.58438 1.5875C7.20938 1.2125 6.7 1.00312 6.16875 1.00312L1.5 1C0.671875 1 0 1.67187 0 2.5ZM3.5 3.5C3.76522 3.5 4.01957 3.60536 4.20711 3.79289C4.39464 3.98043 4.5 4.23478 4.5 4.5C4.5 4.76522 4.39464 5.01957 4.20711 5.20711C4.01957 5.39464 3.76522 5.5 3.5 5.5C3.23478 5.5 2.98043 5.39464 2.79289 5.20711C2.60536 5.01957 2.5 4.76522 2.5 4.5C2.5 4.23478 2.60536 3.98043 2.79289 3.79289C2.98043 3.60536 3.23478 3.5 3.5 3.5Z"
              fill="currentColor"
            />
          </svg>
          <input
            aria-label="Apply a discount"
            className={inputClassName}
            value={code}
            placeholder={formatCartMessage({
              id: 'cart.discount.enter',
              defaultMessage: 'Apply a discount',
            })}
            onChange={handleChange}
            disabled={processing}
          />
          {codeIsInvalid && (
            <span
              className="absolute right-16 top-1/2 -translate-y-1/2"
              onClick={(e) => {
                e.stopPropagation();
                resetCode();
              }}
            >
              <CloseIcon className="size-10 cursor-pointer" />
            </span>
          )}
          {!codeIsInvalid && (
            <span
              className="absolute right-16 top-1/2 -translate-y-1/2 cursor-pointer text-xs text-blue-400 underline"
              onClick={(e) => {
                e.stopPropagation();
                handleSubmit(e);
              }}
            >
              Apply
            </span>
          )}
        </div>
        {codeIsInvalid && (
          <p className="mt-16 font-body text-12 font-medium leading-normal text-accent-red">{erroMessage}</p>
        )}
      </form>

      {discounts && !!discounts.length && (
        <div className={discountsContainerClassName}>
          {discounts.map((discount) => (
            <div
              key={discount.discountId}
              className="mr-2 flex w-fit justify-between gap-8 rounded-sm border border-neutral-400 bg-white px-8 py-4"
            >
              <label className="text-12 uppercase leading-[16px] text-secondary-black">{discount.code}</label>
              <button type="button" onClick={() => handleRemove(discount)}>
                <XMarkIcon className="size-16 text-secondary-black" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default DiscountForm;
